// services/backtestService.js
//
// All calls go through services/http.js (auth header + 401 handling). The
// backend derives the caller from the JWT — never send userId from the client;
// a client-supplied id used to be honoured server-side and was an IDOR.
import http, { API_BASE } from './http';

const API = `${API_BASE}/api/backtest`;

const JSON_HEADERS = { 'Content-Type': 'application/json' };

// ✅ Submit backtest
const submit = async (params) => {
  const res = await http.post(API, params);
  return res.data;
};

// ✅ Save backtest result
async function saveResult(payload) {
  const { data } = await http.post(`${API}/save`, payload);
  return data;
}

// ✅ GET /api/backtest/status/:jobId
const getStatus = async (jobId) => {
  const res = await http.get(`${API}/status/${jobId}`);
  return res.data;
};

// ✅ GET /api/backtest/recent
const getResults = async (limit = 10) => {
  const res = await http.get(`${API}/recent`, { params: { limit } });
  return res.data;
};

// ✅ GET /api/backtest/:id
const getDetail = async (id) => {
  const res = await http.get(`${API}/${id}`);
  return res.data;
};

// ✅ DELETE /api/backtest/:id
const deleteResult = async (id) => {
  const res = await http.delete(`${API}/${id}`);
  return res.data; // { ok: true, id }
};

// ✅ GET /api/backtest/active
const getActive = async () => {
  const res = await http.get(`${API}/active`);
  return res.data; // {active, jobId, status, progress}
};

/**
 * Every test the user has queued or running (a scanner book, a draft test, a
 * backtest, a sweep, a walk-forward): GET /api/backtest/active `jobs` —
 * [{ jobId, kind, label, draftId, botId, scannerId, startedAt, status,
 *    progress, message, lakeWait, waiting, stage? }]. [] from an api without the field.
 * `stage` is a scanner book's step while not terminal ('preparing' |
 * 'resolving_universe' | 'pricing' | 'simulating'); absent on older apis and
 * on other kinds (components/Backtest/runningJobs.js stageLabel words it).
 */
const getRunning = async () => {
  const r = await getActive();
  return Array.isArray(r && r.jobs) ? r.jobs : [];
};

// ✅ Cancel backtest
const cancel = async (jobId) => {
  if (!jobId) throw new Error('Missing jobId');
  try {
    const res = await http.post(`${API}/cancel/${encodeURIComponent(jobId)}`, {});
    return res.data;
  } catch (err) {
    // Fallback for servers that use a JSON body instead of a URL param
    if (err?.response?.status === 404) {
      const res2 = await http.post(`${API}/cancel`, { jobId });
      return res2.data;
    }
    throw err;
  }
};


// ── Feature scan ──
// Does a condition predict anything, before spending a backtest on it? Async:
// POST returns a scanId, the result is polled.
const startFeatureScan = async (payload) => {
  const res = await http.post(`${API}/feature-scan`, payload, { headers: JSON_HEADERS });
  return res.data;
};

const getFeatureScan = async (scanId) => {
  const res = await http.get(`${API}/feature-scan/${encodeURIComponent(scanId)}`);
  return res.data;
};


// ── Parameter sweep ──
// Vary the numbers in a strategy and report the SHAPE of the response.
//
// planSweep is synchronous and runs nothing — it returns the axes the server
// considers tunable plus a combination count. The UI renders exactly what comes
// back rather than guessing which numbers are sweepable, so what is offered and
// what the runner rewrites cannot drift apart.
const planSweep = async (payload) => {
  const res = await http.post(`${API}/sweep/plan`, payload, { headers: JSON_HEADERS });
  return res.data;
};

// The sweep itself is ONE job that runs the whole grid; POST returns a sweepId.
// A 400 here is meaningful (over the combination cap) and carries the count.
const startSweep = async (payload) => {
  const res = await http.post(`${API}/sweep`, payload, { headers: JSON_HEADERS });
  return res.data;
};

const getSweep = async (sweepId) => {
  const res = await http.get(`${API}/sweep/${encodeURIComponent(sweepId)}`);
  return res.data;
};


// ── Multi-symbol batch ──
// One strategy, many symbols. Async: POST returns a searchId, result is polled.
const startBatch = async (payload) => {
  const res = await http.post(`${API}/batch`, payload, { headers: JSON_HEADERS });
  return res.data;
};

const cancelBatch = async (searchId) => {
  const res = await http.post(`${API}/batch/${encodeURIComponent(searchId)}/cancel`, {}, { headers: JSON_HEADERS });
  return res.data;
};

const getBatch = async (searchId) => {
  const res = await http.get(`${API}/batch/${encodeURIComponent(searchId)}`);
  return res.data;
};

// ── Correlation between saved runs ──
// Synchronous — it reads stored equity curves, there is nothing to simulate.
const getCorrelation = async ({ limit = 20, threshold = 0.3 } = {}) => {
  const res = await http.get(`${API}/correlation`, { params: { limit, threshold } });
  return res.data;
};

// ── Joint portfolio backtest over saved runs ──
// Synchronous too: it combines the runs' stored daily returns under one pool
// of capital (sizing rule, rebalance schedule, drawdown control) — nothing
// loads bars or simulates a strategy. `backtestIds` are the caller's own saved
// runs; `sleeves` are series the page already holds. See PortfolioLab.
const buildPortfolio = async (payload) => {
  const res = await http.post(`${API}/portfolio`, payload, { headers: JSON_HEADERS });
  return res.data;
};


// ── Symbol universe ──
// Lives under /api/symbols but is called from the backtest research panels, so
// it sits here where the correct base URL and auth headers already are.
// symbolService.js defaults to port 3000, which is not where the backend runs.
// The named destructure here used to drop every param it did not list, so the
// picker's category, shortable and exchange filters were built by the caller and
// then silently discarded — this path returned the whole venue whatever was
// asked for. Forward what the caller sent instead, minus the blanks.
//
// `quote` in particular must NOT default: the caller omits it deliberately to
// mean "all quotes" (or "equity venue, no such concept"), and a 'USDT' default
// turned both of those into a USDT-only filter.
const getSymbolUniverse = async (opts = {}) => {
  const { limit = 20, rank = 'volume', feeFilter = 'any', ...rest } = opts;
  const params = { limit, rank, feeFilter, ...rest };
  Object.keys(params).forEach((k) => {
    if (params[k] === undefined || params[k] === null || params[k] === '') delete params[k];
  });
  const res = await http.get(`${API_BASE}/api/symbols/universe`, { params });
  return res.data;
};


// ── Funding carry research ──
// Perp funding as an edge source rather than another indicator: a scheduled
// payment rather than a price prediction, which is why it pays for holding
// instead of for trading.
const getFundingResearch = async ({ symbols, source = 'mexc' } = {}) => {
  const res = await http.get(`${API_BASE}/api/research/funding`, {
    params: { symbols: (symbols || []).join(','), source },
  });
  return res.data;
};

// ── Deployment gate ──
// Read-only evaluation of whether a strategy has earned live capital.
const getDeploymentGate = async ({ botId, buy, sell } = {}) => {
  const res = await http.get(`${API_BASE}/api/backtest/deployment-gate`, {
    params: { botId, buy, sell },
  });
  return res.data;
};


// ── Validation funnel ──
// signal -> backtest -> breadth -> gate, stopping at the first failure.
const startValidation = async (payload) => {
  const res = await http.post(`${API_BASE}/api/backtest/validate`, payload, { headers: JSON_HEADERS });
  return res.data;
};

const getValidation = async (id) => {
  const res = await http.get(`${API_BASE}/api/backtest/validate/${encodeURIComponent(id)}`);
  return res.data;
};


// ── Walk-forward / Monte Carlo (backend wave 3-B, feature 7) ──
// Two different execution models on purpose:
//   walk-forward is ONE queued BullMQ job (2N simulations, minutes of CPU) and
//   answers 202 { analysisId } — poll getAnalysis until status is terminal.
//   Monte Carlo resamples an already-saved trade list, so it runs inline and
//   answers 200 with the result attached. Both share the analysis record shape,
//   so the UI polls the same endpoint either way.
const startWalkForward = async (payload) => {
  const res = await http.post(`${API}/walkforward`, payload, { headers: JSON_HEADERS });
  return res.data;
};

const startMonteCarlo = async (payload) => {
  const res = await http.post(`${API}/montecarlo`, payload, { headers: JSON_HEADERS });
  return res.data;
};

// GET /api/backtest/analysis/:id — caller-scoped. While a walk-forward is still
// running the row carries `live`, the raw progress record.
const getAnalysis = async (id) => {
  const res = await http.get(`${API}/analysis/${encodeURIComponent(id)}`);
  return res.data;
};

// GET /api/backtest/analyses/recent — a picker; `result` payloads are omitted.
// Two path segments deliberately: a one-segment /analyses would be swallowed by
// GET /api/backtest/:id and 400 on its UUID guard.
const getRecentAnalyses = async ({ botId, kind, limit = 25 } = {}) => {
  const params = { limit };
  if (botId != null && botId !== '') params.botId = botId;
  if (kind) params.kind = kind;
  const res = await http.get(`${API}/analyses/recent`, { params });
  return res.data;
};

export default {
  submit,
  getStatus,
  saveResult,
  getResults,
  getFundingResearch,
  getDetail,
  deleteResult,
  cancel,
  getActive,
  getRunning,
  startFeatureScan,
  getFeatureScan,
  planSweep,
  startSweep,
  getSweep,
  startBatch,
  cancelBatch,
  getBatch,
  buildPortfolio,
  getCorrelation,
  getSymbolUniverse,
  getDeploymentGate,
  startValidation,
  getValidation,
  startWalkForward,
  startMonteCarlo,
  getAnalysis,
  getRecentAnalyses
};
